/**
 * useCollaborativeGame
 *
 * Manages live state for a collaborative game session (Buy a Feature, Speed Boat, etc.)
 * using Supabase Realtime. All participants see the same state in real time.
 *
 * State is derived entirely from the ordered event log — this is an event-sourcing
 * approach. The event log IS the record of the session; the derived state is a view
 * over it. This makes the transcript first-class rather than an afterthought.
 *
 * Usage:
 *   const game = useCollaborativeGame(sessionId, myName);
 *   game.purchase(featureId, optionalNote);
 *   game.removePurchase(featureId);
 *   game.addComment(text);
 *   game.markDone();
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from '../lib/supabaseClient';
import {
  getSessionById,
  getSessionEvents,
  recordEvent,
  updateSessionStatus,
} from '../lib/collaborativeGameService';
import type {
  CollaborativeGameSession,
  CollaborativeGameState,
  GameEvent,
  LiveFeatureState,
  LiveParticipant,
  BuyAFeatureConfig,
} from '../types/collaborativeGame';

// Derive the live in-memory state from the ordered event log
function deriveState(
  session: CollaborativeGameSession,
  events: GameEvent[]
): Pick<CollaborativeGameState, 'participants' | 'features'> {
  const config = session.config as BuyAFeatureConfig;
  const budgetPerParticipant = config.budget_per_participant ?? 0;
  const participantsMap = new Map<string, LiveParticipant>();
  const featuresMap = new Map<string, LiveFeatureState>();

  // Seed features from config
  if (config.features) {
    for (const f of config.features) {
      featuresMap.set(f.id, {
        id: f.id,
        name: f.name,
        price: f.price,
        totalPurchases: 0,
        totalSpent: 0,
        buyerNames: [],
      });
    }
  }

  for (const event of events) {
    const name = event.participantName;

    if (event.eventType === 'join') {
      if (!participantsMap.has(name)) {
        const data = event.eventData as { isHost?: boolean };
        participantsMap.set(name, {
          name,
          isHost: data.isHost ?? false,
          isDone: false,
          joinedAt: event.createdAt,
          purchases: [],
          remainingBudget: budgetPerParticipant,
        });
      }
    }

    if (event.eventType === 'purchase') {
      const data = event.eventData as { featureId: string; price: number };
      const participant = participantsMap.get(name);
      const feature = featuresMap.get(data.featureId);
      if (participant && feature) {
        const existing = participant.purchases.find(p => p.featureId === data.featureId);
        if (existing) {
          existing.quantity += 1;
        } else {
          participant.purchases.push({ featureId: data.featureId, quantity: 1 });
        }
        participant.remainingBudget -= data.price;
        feature.totalPurchases += 1;
        feature.totalSpent += data.price;
        if (!feature.buyerNames.includes(name)) {
          feature.buyerNames.push(name);
        }
      }
    }

    if (event.eventType === 'remove_purchase') {
      const data = event.eventData as { featureId: string; price: number };
      const participant = participantsMap.get(name);
      const feature = featuresMap.get(data.featureId);
      if (participant && feature) {
        const existing = participant.purchases.find(p => p.featureId === data.featureId);
        if (existing && existing.quantity > 0) {
          existing.quantity -= 1;
          if (existing.quantity === 0) {
            participant.purchases = participant.purchases.filter(p => p.featureId !== data.featureId);
          }
          participant.remainingBudget += data.price;
          feature.totalPurchases = Math.max(0, feature.totalPurchases - 1);
          feature.totalSpent = Math.max(0, feature.totalSpent - data.price);
          // Recompute buyerNames from remaining purchases
          if (!participant.purchases.find(p => p.featureId === data.featureId)) {
            feature.buyerNames = feature.buyerNames.filter(n => n !== name);
          }
        }
      }
    }

    if (event.eventType === 'done') {
      const participant = participantsMap.get(name);
      if (participant) participant.isDone = true;
    }
  }

  return {
    participants: Array.from(participantsMap.values()),
    features: Array.from(featuresMap.values()),
  };
}

export interface CollaborativeGameActions {
  purchase: (featureId: string, note?: string) => Promise<void>;
  removePurchase: (featureId: string) => Promise<void>;
  addComment: (text: string) => Promise<void>;
  markDone: () => Promise<void>;
  startSession: () => Promise<void>;
  endSession: () => Promise<void>;
}

export type UseCollaborativeGameResult = CollaborativeGameState & CollaborativeGameActions;

export function useCollaborativeGame(
  sessionId: string | null,
  myName: string
): UseCollaborativeGameResult {
  const [session, setSession] = useState<CollaborativeGameSession | null>(null);
  const [events, setEvents] = useState<GameEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  // Load initial session + events
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    (async () => {
      try {
        const [sess, evts] = await Promise.all([
          getSessionById(sessionId),
          getSessionEvents(sessionId),
        ]);
        if (cancelled) return;
        setSession(sess);
        setEvents(evts);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load session');
      }
    })();

    return () => { cancelled = true; };
  }, [sessionId]);

  // Subscribe to Realtime updates
  useEffect(() => {
    if (!sessionId) return;
    const supabase = getSupabaseClient();
    const channelName = `collab-game-${sessionId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'collaborative_game_events', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const newEvent: GameEvent = {
            id: row.id as string,
            sessionId: row.session_id as string,
            sequenceNum: row.sequence_num as number,
            participantName: row.participant_name as string,
            eventType: row.event_type as GameEvent['eventType'],
            eventData: (row.event_data as GameEvent['eventData']) || {},
            createdAt: row.created_at as string,
          };
          setEvents(prev => {
            // Avoid duplicate insertions
            if (prev.find(e => e.id === newEvent.id)) return prev;
            return [...prev, newEvent].sort((a, b) => a.sequenceNum - b.sequenceNum);
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'collaborative_game_sessions', filter: `id=eq.${sessionId}` },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          setSession(prev => prev ? {
            ...prev,
            status: row.status as CollaborativeGameSession['status'],
            updatedAt: row.updated_at as string,
          } : prev);
        }
      )
      .subscribe((status) => {
        setIsConnected(status === 'SUBSCRIBED');
        if (status === 'CHANNEL_ERROR') setError('Realtime connection error');
      });

    channelRef.current = channel;
    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [sessionId]);

  const getFeature = useCallback((featureId: string) => {
    const config = session?.config as BuyAFeatureConfig | undefined;
    return config?.features?.find(f => f.id === featureId);
  }, [session]);

  const purchase = useCallback(async (featureId: string, note?: string) => {
    if (!sessionId || !myName) return;
    const feature = getFeature(featureId);
    if (!feature) return;
    await recordEvent(sessionId, myName, 'purchase', {
      featureId,
      featureName: feature.name,
      price: feature.price,
      ...(note ? { note } : {}),
    });
  }, [sessionId, myName, getFeature]);

  const removePurchase = useCallback(async (featureId: string) => {
    if (!sessionId || !myName) return;
    const feature = getFeature(featureId);
    if (!feature) return;
    await recordEvent(sessionId, myName, 'remove_purchase', {
      featureId,
      featureName: feature.name,
      price: feature.price,
    });
  }, [sessionId, myName, getFeature]);

  const addComment = useCallback(async (text: string) => {
    if (!sessionId || !myName) return;
    await recordEvent(sessionId, myName, 'comment', { text });
  }, [sessionId, myName]);

  const markDone = useCallback(async () => {
    if (!sessionId || !myName) return;
    await recordEvent(sessionId, myName, 'done', {});
  }, [sessionId, myName]);

  const startSession = useCallback(async () => {
    if (!sessionId || !myName) return;
    await updateSessionStatus(sessionId, 'active');
    await recordEvent(sessionId, myName, 'session_started', {});
  }, [sessionId, myName]);

  const endSession = useCallback(async () => {
    if (!sessionId || !myName) return;
    await updateSessionStatus(sessionId, 'ended');
    await recordEvent(sessionId, myName, 'session_ended', {});
  }, [sessionId, myName]);

  const derived = session ? deriveState(session, events) : { participants: [], features: [] };

  return {
    session,
    events,
    isConnected,
    error,
    ...derived,
    purchase,
    removePurchase,
    addComment,
    markDone,
    startSession,
    endSession,
  };
}
